import {Engine} from "platform/engine/Engine";
import {
    DoChangeCloseOutMethodPayload,
    DoChangeLeveragePayload,
    InsertClientPreferencePayload,
    SetAvailableCloseOutMethods,
    SetAvailableLeverages,
    SetClientPreference,
    SetCurrentCloseOutMethod,
    SetCurrentLeverage,
    SetDepositLimitsInfo, SetIsRollingContractsEnabled,
    SetUserProfileInfo,
    SetVaultDetails,
    SubmitAccountAnnualReportPayload
} from "core/redux/settings/SettingsReduxActions";
import {AuthState} from "core/state/AuthState";
import Platform from "platform/Platform";
import {ServiceType} from "enum/ServiceType";
import {AccountState} from "core/state/AccountState";
import {ServerType} from "platform/enum/ServerType";
import {NXEnvironmentType} from "platform/protocol/enum/NXEnvironmentType";
import {PreferencesManager} from "core/engine/PreferencesManager";
import {Preference} from "core/util/Preference";
import {XhrUtil} from "core/util/XhrUtil";
import {HttpReject} from "platform/network/http/Http";
import Utils from "platform/util/Utils";
import {GetAccountLogRequest} from "protocol/account/GetAccountLogRequest";
import {ShowPopup} from "platform/redux/popups/PopupsActions";
import {PopupActionType, PopupIconType, PopupType} from "platform/redux/popups/PopupsReduxState";
import {TranslationKey} from "enum/TranslationKey";
import {TranslationParam} from "enum/TranslationParam";
import Parameter from "platform/util/Parameter";
import {StoreState} from "core/redux/StoreState";
import {GetChangeLeverageRequest} from "protocol/config/GetChangeLeverageRequest";
import {LangCode} from "platform/enum/LangCode";
import {LanguageUtil} from "platform/util/LanguageUtil";
import {GetChangeCloseOutMethodsRequest} from "protocol/config/GetChangeCloseOutMethodsRequest";
import {GetChangeLeverageResponse, Leverage} from "protocol/config/GetChangeLeverageResponse";
import {CloseOutMethod, GetChangeCloseOutMethodsResponse} from "protocol/config/GetChangeCloseOutMethodsResponse";
import {ChangeLeverageRequest} from "protocol/config/ChangeLeverageRequest";
import {ChangeCloseOutMethodRequest} from "protocol/config/ChangeCloseOutMethodRequest";
import {HideLoader, SetLoader} from "platform/redux/core/CoreActions";
import {LoaderType} from "platform/enum/LoaderType";
import {SetTradingModal} from "core/redux/app/AppReduxActions";
import TradingModalType from "enum/TradingModalType";
import {AgreeLeverageDisclaimerRequest} from "protocol/config/AgreeLeverageDisclaimerRequest";
import {GetDepositLimitsResponse} from "protocol/account/GetDepositLimitsResponse";
import {GetUserProfileResponse, UserProfileInfo} from "protocol/account/GetUserProfileResponse";
import {GetVaultDetailsResponse} from "protocol/promo/GetVaultDetailsResponse";
import {IsRollingEnabledResponse} from "protocol/account/IsRollingEnabledResponse";

export default class SettingsEngine extends Engine {

    private static _instance: SettingsEngine;

    public static instance(): SettingsEngine {
        return this._instance || (this._instance = new this());
    }

    public async setup(): Promise<void> {
        await super.setup();
    }

    public doFetchDepositLimits = async (): Promise<void> => {
        const answer: [HttpReject, GetDepositLimitsResponse] = await Utils.to(this.sendToWebProfitService(null, "GetUserDepositLimits"));
        if (answer[0]) {
            this._logger.debug("Failed fetch deposit limits");
        } else {
            Platform.dispatch(SetDepositLimitsInfo({
                accountLimit: answer[1]?.MaxNetDepositsAmount,
                leftTopUp: answer[1]?.MaxNetDepositsAmount - answer[1]?.TotalDepositsAmount
            }))
        }
    }

    public doFetchUserProfile = async (): Promise<UserProfileInfo> => {
        const answer: [HttpReject, GetUserProfileResponse] = await Utils.to(this.sendToWebProfitService(null, "GetUserProfileConfiguration"));
        if (answer[0]) {
            this._logger.debug("Failed fetch user profile");
        } else {
            Platform.dispatch(SetUserProfileInfo({
                userProfile: answer[1] || {}
            }));
            return Promise.resolve(answer[1] || {});
        }
        return Promise.resolve({});
    }

    public doFetchSettingsConfig = async (): Promise<void> => {
        const languageCode: LangCode = LanguageUtil.languageCode();
        this.doFetchLeverages().catch(() => {});
        const request: GetChangeCloseOutMethodsRequest = {languageCode};
        this.sendToComplianceService(request, "GetChangeCloseOutMethods").then((response: GetChangeCloseOutMethodsResponse) => {
            const currentMethod: CloseOutMethod = response.CloseOutMethods?.filter(method => method.IsSelected)[0];
            Platform.dispatch(SetAvailableCloseOutMethods({
                isAllowed: response.IsAllowed,
                methods: response.CloseOutMethods || [],
                currentMethod
            }));
        });
    }

    public doInsertClientPreference = async (payload: InsertClientPreferencePayload<any>): Promise<void> => {
        const authState: AuthState = Platform.state(ServiceType.Auth);
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const serverType: ServerType = accountState.accountType === NXEnvironmentType.Live ? ServerType.TradingReal : ServerType.TradingDemo;
        const origin: string = Platform.config().servers[serverType];
        const preference: Preference<any> = payload.preference;
        const value: string = Preference.serialize(payload.key, preference);
        const result: boolean = await PreferencesManager.InsertPreference(origin, authState.token, accountState.accountId, accountState.accountType, payload.key, value);
        if (result) {
            Platform.dispatch(SetClientPreference({
                key: payload.key,
                preference
            }));
        }
        return Promise.resolve();
    }

    public isCanSkipEducationTradingGroup = async (): Promise<boolean> => {
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const answer: [HttpReject, boolean] = await Utils.to(this.sendToTradeService(accountState.accountId, "CanSkipEducationTradingGroup"));
        return Promise.resolve(Utils.parseBoolean(answer[1]));
    }

    private doFetchLeverages = async (): Promise<void> => {
        const languageCode: LangCode = LanguageUtil.languageCode();
        const request: GetChangeLeverageRequest = {languageCode};
        const answer: [HttpReject, GetChangeLeverageResponse] = await Utils.to(this.sendToComplianceService(request, "GetChangeLeverage"));
        if (answer[1]) {
            const {Success, AvailableLeverages, SelectedLeverage, ShouldAcceptDisclaimer} = answer[1];
            Platform.dispatch(SetAvailableLeverages({
                isAllowed: Success,
                leverages: AvailableLeverages || [],
                leverage: SelectedLeverage,
                shouldAcceptLeverageDisclaimer: ShouldAcceptDisclaimer
            }));
        }
    }

    public doSubmitLeverageDisclaimer = async (): Promise<void> => {
        const languageCode: LangCode = LanguageUtil.languageCode();
        const request: AgreeLeverageDisclaimerRequest = {languageCode};
        const answer: [HttpReject, GetChangeLeverageResponse] = await Utils.to(this.sendToComplianceService(request, "AcceptDisclaimer"));
        if (answer[0]) {
            XhrUtil.notifyReject(answer[0]);
        } else {
            const {Success, AvailableLeverages, SelectedLeverage, ShouldAcceptDisclaimer} = answer[1];
            Platform.dispatch(SetAvailableLeverages({
                isAllowed: Success,
                leverages: AvailableLeverages || [],
                leverage: SelectedLeverage,
                shouldAcceptLeverageDisclaimer: ShouldAcceptDisclaimer
            }));
            if (Success) {
                Platform.dispatch(
                    SetTradingModal({
                        tradingModalType: TradingModalType.MyLeverageDisclaimer,
                        info: {
                            visible: false,
                        },
                    })
                );
                Platform.dispatch(
                    SetTradingModal({
                        tradingModalType: TradingModalType.MyLeverage,
                        info: {
                            visible: true,
                        },
                    })
                );
            } else {
                Platform.dispatch(ShowPopup({
                    popup: {
                        type: PopupType.ERROR,
                        message: {
                            trKey: TranslationKey.errorGeneral
                        },
                        showClose: true,
                        icon: PopupIconType.ERROR,
                        actions: [{type: PopupActionType.OK}]
                    }
                }));
            }
        }
    }

    public doChangeLeverage = async ({leverageId}: DoChangeLeveragePayload): Promise<void> => {
        const request: ChangeLeverageRequest = {selectedLeverageId: leverageId};
        Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
        const answer: [HttpReject, {Message: string; Success: boolean}] = await Utils.to(this.sendToComplianceService(request, "ChangeCurrentLeverage"));
        Platform.dispatch(HideLoader({}));
        if (answer[0]) {
            XhrUtil.notifyReject(answer[0]);
        } else {
            if (answer[1].Success) {
                const {leverages} = Platform.reduxState<StoreState>().settings;
                const leverage: Leverage = leverages?.filter(l => l.Id === leverageId)[0];
                Platform.dispatch(SetCurrentLeverage({
                    leverage
                }));
                Platform.dispatch(
                    SetTradingModal({
                        tradingModalType: TradingModalType.MyLeverage,
                        info: {
                            visible: false,
                        },
                    })
                );
                Platform.dispatch(ShowPopup({
                    popup: {
                        type: PopupType.INFO,
                        icon: PopupIconType.INFO,
                        message: {
                            trKey: TranslationKey.leverageChanged,
                            params: [Parameter.Of(TranslationParam.value, `1 : ${leverage.Value}`)]
                        },
                        showClose: true,
                        actions: [{type: PopupActionType.OK}]
                    }
                }));
            } else {
                Platform.dispatch(ShowPopup({
                    popup: {
                        type: PopupType.ERROR,
                        message: {
                            trKey: TranslationKey.errorGeneral
                        },
                        showClose: true,
                        icon: PopupIconType.ERROR,
                        actions: [{type: PopupActionType.OK}]
                    }
                }));
            }
        }
    }

    public doChangeCloseOutMethod = async ({methodId}: DoChangeCloseOutMethodPayload): Promise<void> => {
        const request: ChangeCloseOutMethodRequest = {selectedCloseOutMethod: methodId};
        Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
        const answer: [HttpReject, {Message: string; Success: boolean}] = await Utils.to(this.sendToComplianceService(request, "ChangeCurrentCloseOutMethod"));
        Platform.dispatch(HideLoader({}));
        if (answer[0]) {
            XhrUtil.notifyReject(answer[0]);
        } else {
            if (answer[1].Success) {
                const {closeOutMethods} = Platform.reduxState<StoreState>().settings;
                const currentMethod: CloseOutMethod = closeOutMethods?.filter(method => method.Value === Number(methodId))[0];
                Platform.dispatch(SetCurrentCloseOutMethod({
                    currentMethod
                }));
                Platform.dispatch(
                    SetTradingModal({
                        tradingModalType: TradingModalType.CloseOutMethod,
                        info: {
                            visible: false,
                        },
                    })
                );
                Platform.dispatch(ShowPopup({
                    popup: {
                        type: PopupType.INFO,
                        icon: PopupIconType.INFO,
                        message: {
                            trKey: TranslationKey.closeOutMethodChanged,
                            params: [Parameter.Of(TranslationParam.value, currentMethod?.Text)]
                        },
                        showClose: true,
                        actions: [{type: PopupActionType.OK}]
                    }
                }));
            } else {
                Platform.dispatch(ShowPopup({
                    popup: {
                        type: PopupType.ERROR,
                        message: {
                            trKey: TranslationKey.errorGeneral
                        },
                        showClose: true,
                        icon: PopupIconType.ERROR,
                        actions: [{type: PopupActionType.OK}]
                    }
                }));
            }
        }
    }

    public doSubmitAccountAnnualReport = async ({year}: SubmitAccountAnnualReportPayload): Promise<void> => {
        this._logger.debug("Do submit annual report for: " + year);
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const request: GetAccountLogRequest = {
            AccountId: accountState.accountId,
            from: "{year}-01-01T00:00:00.000Z".replace("{year}", year.toString()),
            to: "{year}-12-31T00:00:00.000Z".replace("{year}", year.toString()),
            filterActionTypes: []
        };
        const answer: [HttpReject, []] = await Utils.to(XhrUtil.sendAuthenticated(request, "TradeServer/SearchLogProvider.svc/json/GetAccountDealingLog"));
        if (answer[0]) {
            XhrUtil.notifyReject(answer[0]);
        } else {
            const {userProfile} = Platform.reduxState<StoreState>().settings;
            let email: string = userProfile?.Email;
            if (Utils.isEmpty(email)) {
                const profile: UserProfileInfo = await this.doFetchUserProfile();
                email = profile?.Email;
            }
            Platform.dispatch(ShowPopup({
                popup: {
                    type: PopupType.INFO,
                    message: {
                        trKey: Utils.isNotNull(answer[1]) ? TranslationKey.annualReportSuccess : TranslationKey.annualReportPending,
                        params: [Parameter.Of(TranslationParam.value, email)]
                    },
                    showClose: true,
                    icon: PopupIconType.SUCCESS,
                    actions: [{type: PopupActionType.OK}]
                }
            }));
        }
    }

    public doFetchVaultDetails = async () => {
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const answer: [HttpReject, GetVaultDetailsResponse] = await Utils.to(this.sendToAccountService(accountState.accountId, "GetVaultDetails"));
        if (answer[0]) {
            this._logger.debug("Failed fetch vault details");
        } else {
            Platform.dispatch(SetVaultDetails({
                vault: answer[1] || {}
            }));
        }
    }

    public doRollingContractEnabled = async () => {
        const answer: [HttpReject, IsRollingEnabledResponse] = await Utils.to(this.sendToWebProfitService(null, "IsRollingEnabled"));
        if (answer[0]) {
            this._logger.debug("Failed fetch rolling status");
        } else {
            Platform.dispatch(SetIsRollingContractsEnabled({
                enabled: answer[1]?.IsRollingEnabled,
                expiration: answer[1]?.RollingContractsExpirationDate
            }))
        }
    }

    private sendToAccountService = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticated(request, "WebProfitServer/AccountStatesService.svc/json/" + path);
    }

    private sendToTradeService = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticated(request, "TradeServer/TradeService.svc/json/" + path);
    }

    private sendToComplianceService = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticatedTo(ServerType.Compliance, request, "ComplianceWebSite/ComplianceFormService.svc/json/" + path);
    }

    private sendToWebProfitService = (request: any, path: string): Promise<any> => {
        return XhrUtil.sendAuthenticated(request, "WebProfitServer/WebProfitClientService.svc/json/" + path);
    }
}
